import { copilotState } from '@/lib/copilot-state';
import { cn } from '@/lib/utils';
import { SparklesIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useDialog } from '@/hooks/useDialog';
import { useNavigate, useParams } from 'react-router-dom';
import { CopilotPlugin } from '@udecode/plate-ai/react';
import { updateChapterTitle } from '@/services/supabase/chapters';
import { toast } from 'sonner';

export function CopilotStatus({ editor, className }: { editor?: any, className?: string }) {
    const [isThinking, setIsThinking] = useState(false);
    const { openDialog } = useDialog();
    const navigate = useNavigate();
    const { projectId } = useParams();

    useEffect(() => {
        // Subscribe to state changes
        const unsubscribe = copilotState.subscribe((thinking) => {
            setIsThinking(thinking);
        });

        return unsubscribe;
    }, []);

    const handleSuggest = async () => {
        if (!editor || isThinking) return;

        // Validation Check: Ensure there's enough context
        const options = editor.getOptions(CopilotPlugin) as any;
        const { chapterBeats = [], chapterDescription = '', chapterId, chapterTitle } = options || {};
        
        const beatsCount = chapterBeats?.length || 0;
        const hasOutline = !!chapterDescription && chapterDescription !== '-' && chapterDescription.trim().length > 0;

        if (beatsCount < 2 && !hasOutline) {
            openDialog('copilot-warning', {
                projectId,
                chapterId,
                beatsCount,
                hasOutline,
                onPlanChapter: () => {
                    navigate(`/projects/${projectId}/chapters/plan-scenes`);
                },
                onWriteOutline: () => {
                   openDialog('form', {
                       title: "Chapter Outline",
                       description: "Write a high-level outline to give the AI context about what happens in this chapter.",
                       fields: [
                           { 
                               name: "description", 
                               label: "Outline", 
                               type: "textarea", 
                               placeholder: "What happens in this chapter? Who is involved?",
                               required: true,
                               defaultValue: chapterDescription === '-' ? '' : chapterDescription
                           }
                       ],
                       submitText: "Save Outline"
                   }, async (data: { description: string }) => {
                       try {
                           if (!chapterId) return;
                           await updateChapterTitle(Number(chapterId), chapterTitle, data.description);
                           toast.success("Outline saved", { description: "You can now try suggesting again with better context!" });
                           
                           // Refresh editor options locally so it doesn't stay stale for the next click
                           // Note: Real state sync happens on parent refresh, but this helps the local validation
                           options.chapterDescription = data.description;
                       } catch (error) {
                           toast.error("Failed to save outline");
                       }
                   });
                }
            });
            return;
        }

        // Ensure the editor has focus and a selection before triggering copilot,
        // otherwise getPrompt/triggerQuery APIs can throw silent exceptions.
        if (!editor.selection) {
            editor.tf.focus();
            const lastBlockIndex = editor.children.length - 1;
            if (lastBlockIndex >= 0) {
                editor.tf.select([lastBlockIndex]);
                editor.tf.collapse({ edge: 'end' });
            }
        }

        // Intended Flow: trigger the copilot suggestion command natively
        // without falling back to debounced ghost-typing or async timeouts.
        if (editor.api?.copilot?.triggerSuggestion) {
            editor.api.copilot.triggerSuggestion();
        } else {
            console.warn('CopilotPlugin is not fully initialized or missing triggerSuggestion command.');
        }
    };

    const handleStop = () => {
        if (!editor) return;

        if (editor.api?.copilot?.stop) {
            editor.api.copilot.stop();
        }

        // Force state reset so the user isn't stuck visually if the API hangs
        copilotState.setThinking(false);
    };

    if (isThinking) {
        return (
            <div className={cn("flex items-center gap-2", className)}>
                <div className="relative flex items-center justify-center">
                    <SparklesIcon className="h-3.5 w-3.5 text-blue-500 animate-pulse" />
                    <span className="absolute inset-0 rounded-full bg-blue-500/30 animate-ping" />
                </div>
                <span className="text-[10px] font-medium text-blue-500 animate-pulse uppercase tracking-wider">
                    Thinking...
                </span>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleStop}
                    className="h-4 w-4 ml-1 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    title="Stop brainstorming"
                >
                    <XIcon className="h-2.5 w-2.5" />
                </Button>
            </div>
        );
    }

    return (
        <Button
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSuggest}
            className={cn(
                "h-6 px-2 text-[10px] font-medium text-blue-500 hover:text-blue-600 hover:bg-blue-500/10 gap-1.5 rounded-full uppercase tracking-wider transition-colors",
                className
            )}
            title="Generate AI suggestion at cursor"
        >
            <SparklesIcon className="h-3 w-3" />
            Suggest
        </Button>
    );
}
